import React, { useState, useEffect, useCallback } from 'react';
import { Package, Zap, Shield, Brain, Network, FileText, Activity, RefreshCw, CheckCircle, AlertCircle, XCircle, Key } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ModulesConfiguration = () => {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, enabled_count: 0, healthy_count: 0 });
  const [toggling, setToggling] = useState(null); // Track which module is being toggled

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/system/modules`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setError(null);
        setModules(data.modules || {});
        setStats({
          total: data.total || 0,
          enabled_count: data.enabled_count || 0,
          healthy_count: data.healthy_count || 0,
        });
      }
    } catch (err) {
      console.error('Modules fetch error:', err);
      setError(`Erreur de connexion: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const toggleModule = async (moduleId) => {
    const module = modules[moduleId];
    if (!module) return;

    // Don't allow disabling core module
    if (moduleId === 'core' && module.enabled) {
      alert('Le module Core ne peut pas etre desactive');
      return;
    }

    setToggling(moduleId);

    try {
      const response = await fetch(`${API_BASE}/api/v1/system/modules/${moduleId}/toggle`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled: !module.enabled }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      // Update local state optimistically
      setModules((prev) => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          enabled: !prev[moduleId].enabled,
        },
      }));

      // Update stats
      setStats((prev) => ({
        ...prev,
        enabled_count: prev.enabled_count + (module.enabled ? -1 : 1),
      }));
    } catch (err) {
      console.error('Toggle error:', err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setToggling(null);
    }
  };

  const getModuleIcon = (moduleId) => {
    const icons = {
      core: Package,
      scanning: Network,
      ai: Brain,
      exploitation: Zap,
      reporting: FileText,
      intelligence: Activity,
      websocket: Activity,
      bruteforce: Shield,
      credentials: Key,
    };
    return icons[moduleId] || Package;
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'degraded':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'unhealthy':
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-400';
      case 'degraded':
        return 'text-yellow-400';
      case 'unhealthy':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Translate component names to French
  const translateComponentName = (name) => {
    const translations = {
      // Core
      'FastAPI': 'FastAPI',
      'PostgreSQL': 'PostgreSQL',
      'Redis': 'Redis',
      'WebSocket': 'WebSocket',
      'Celery': 'Celery',
      // Scanning
      'Nmap Adapter': 'Adaptateur Nmap',
      'Nuclei Adapter': 'Adaptateur Nuclei',
      'Parser XML': 'Analyseur XML',
      'WPScan': 'WPScan',
      // AI
      'Ollama Client': 'SYLink AI',
      'LLM Orchestrator': 'Orchestrateur LLM',
      'Prompt Templates': 'Modèles de Prompts',
      'Scan Analyzer': 'Analyseur de Scans',
      // Exploitation
      'Metasploit Adapter': 'Adaptateur Metasploit',
      'Exploit Matcher': 'Correspondance Exploits',
      'Payload Generator': 'Générateur de Payloads',
      'Session Manager': 'Gestionnaire de Sessions',
      // Reporting
      'Report Generator': 'Générateur de Rapports',
      'PDF Generator': 'Générateur PDF',
      'Export Engine': 'Moteur d\'Export',
      'SIEM Connectors': 'Connecteurs SIEM',
      // Intelligence
      'Threat Intel Feeds': 'Flux Threat Intel',
      'ML Predictor': 'Prédicteur ML',
      'GraphQL API': 'API GraphQL',
      'Metrics Exporter': 'Exportateur Métriques',
      // WebSocket
      'WebSocket Manager': 'Gestionnaire WebSocket',
      'Event Broadcaster': 'Diffuseur d\'Événements',
      'Subscription Manager': 'Gestionnaire d\'Abonnements',
      // Bruteforce
      'Hydra Manager': 'Gestionnaire Hydra',
      'Wordlist Manager': 'Gestionnaire de Wordlists',
      'Credential Validator': 'Validateur de Credentials',
      // Credentials
      'Credential Storage': 'Stockage des Credentials',
      'Validator': 'Validateur',
      'Export Manager': 'Gestionnaire d\'Export',
    };
    return translations[name] || name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-brand-green animate-spin" />
        <span className="ml-3 text-gray-400">Chargement des modules...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <div className="text-red-400 font-medium">Erreur de chargement</div>
          <div className="text-sm text-gray-400">{error}</div>
        </div>
        <button
          onClick={fetchModules}
          className="ml-auto px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">Configuration des Modules</h2>
        <button
          onClick={fetchModules}
          disabled={loading}
          className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Modules list */}
      {Object.entries(modules).map(([moduleId, module]) => {
        const ModuleIcon = getModuleIcon(moduleId);
        const isToggling = toggling === moduleId;

        return (
          <div
            key={moduleId}
            className={`bg-brand-black rounded-lg border-2 transition-all ${
              module.enabled ? 'border-brand-green/30' : 'border-gray-800'
            }`}
          >
            {/* Header */}
            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    module.enabled ? 'bg-brand-green/20' : 'bg-gray-800'
                  }`}
                >
                  <ModuleIcon
                    className={`w-6 h-6 ${module.enabled ? 'text-brand-green' : 'text-gray-500'}`}
                  />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-bold text-white">{module.name}</h3>
                    <span className="text-xs text-gray-500">v{module.version}</span>
                    {module.overall_status && (
                      <span className={`flex items-center gap-1 text-xs ${getHealthStatusColor(module.overall_status)}`}>
                        {getHealthStatusIcon(module.overall_status)}
                        {module.overall_status}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">{module.description}</p>
                </div>
              </div>

              <button
                onClick={() => toggleModule(moduleId)}
                disabled={isToggling || (moduleId === 'core' && module.enabled)}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                  module.enabled ? 'bg-brand-green' : 'bg-gray-600'
                } ${isToggling ? 'opacity-50' : ''} ${
                  moduleId === 'core' && module.enabled ? 'cursor-not-allowed opacity-70' : ''
                }`}
              >
                {isToggling ? (
                  <RefreshCw className="w-4 h-4 text-white mx-auto animate-spin" />
                ) : (
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                      module.enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                )}
              </button>
            </div>

            {/* Details (if enabled) */}
            {module.enabled && (
              <div className="px-5 pb-5 border-t border-gray-800">
                <div className="pt-4 space-y-4">
                  {/* Components */}
                  {module.components && module.components.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-400 mb-2">Composants</h4>
                      <div className="flex flex-wrap gap-2">
                        {module.components.map((comp, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-brand-green/10 text-brand-green text-xs rounded border border-brand-green/30"
                          >
                            {translateComponentName(comp)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Health Checks */}
                  {module.health && Object.keys(module.health).length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-400 mb-2">État des Services</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(module.health).map(([service, health]) => (
                          <div
                            key={service}
                            className={`flex items-center gap-2 p-2 rounded bg-brand-black-lighter ${
                              health.status === 'healthy'
                                ? 'border-l-2 border-green-500'
                                : health.status === 'unhealthy'
                                ? 'border-l-2 border-red-500'
                                : 'border-l-2 border-gray-500'
                            }`}
                          >
                            {getHealthStatusIcon(health.status)}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-white capitalize truncate">{service}</div>
                              <div className="text-xs text-gray-500 truncate">{health.message}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Configuration */}
                  {module.config && Object.keys(module.config).length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-400 mb-2">Configuration</h4>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {Object.entries(module.config).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex justify-between bg-brand-black-lighter p-2 rounded"
                          >
                            <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                            <span className="text-white font-mono">
                              {typeof value === 'boolean'
                                ? value
                                  ? 'Oui'
                                  : 'Non'
                                : Array.isArray(value)
                                ? value.join(', ')
                                : value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Summary */}
      <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Package className="w-6 h-6 text-brand-green flex-shrink-0" />
          <div className="text-sm text-gray-300">
            <div className="font-medium text-brand-green mb-1">Modules Actifs</div>
            <div>
              {stats.enabled_count} sur {stats.total} modules actifs.{' '}
              {stats.healthy_count === stats.enabled_count ? (
                <span className="text-green-400">Tous les services sont operationnels.</span>
              ) : (
                <span className="text-yellow-400">
                  {stats.healthy_count} modules en bon etat.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesConfiguration;
